import { AvailableIcons } from '@asn/icon';

import { Component, ViewEncapsulation, output, input, inject, signal, DOCUMENT, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { NgClass } from '@angular/common';

import { ApContentPipe, ApContentService } from '@ap/content';
import { ApThemeService } from '@ap/theme';
import { ApIllustrationComponent } from '@ap/illustration';
import '@asn/badge';
import '@asn/icon';

import { RollDownUpAnimation } from './header.component.animations';
import { NavigationItem, NavigationType, ProfileMenu, ProfileMenuItem } from './header.model';
import { ApHamburgerComponent } from './hamburger/hamburger.component';
import { ApSearchIconComponent } from './search-icon/search-icon.component';
import { ApHorizontalMenuComponent } from './horizontal-menu/horizontal-menu.component';
import { ApVerticalMenuComponent } from './vertical-menu/vertical-menu.component';
import { ApProfileMenuComponent } from './profile-menu/profile-menu.component';
import content from './header.content.json';

@Component({
    selector: 'ap-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    animations: [RollDownUpAnimation],
    encapsulation: ViewEncapsulation.Emulated,
    imports: [
        ApContentPipe,
        NgClass,
        ApIllustrationComponent,
        ApHamburgerComponent,
        ApSearchIconComponent,
        ApHorizontalMenuComponent,
        ApVerticalMenuComponent,
        ApProfileMenuComponent
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ApHeaderComponent {
    private readonly contentService = inject(ApContentService);
    private readonly themeService = inject(ApThemeService);

    private document = inject(DOCUMENT);

    /**
     * Add a description to the icon for accessibility
     */
    public readonly logoAriaLabel = input<string>();

    /**
     * If filled, asn-icon will be shown at the end of the menu on the right.
     * No other navigation will be shown
     */
    public readonly icon = input<AvailableIcons>();

    /**
     * Add a href to the logo so it can be clicked.
     */
    public readonly logoLink = input<string>();

    /**
     * The layout strategy to use. Can be either empty, small (to match the application canvas-small layout), but also medium.
     */
    public readonly layout = input<'small' | 'medium' | 'default'>('default');

    /**
     * If header needs to be standalone, fill with {string} 'standalone'
     */
    public readonly variant = input<'standalone'>();

    /**
     * The navigation items
     */
    public readonly navigationItems = input<NavigationItem[]>([]);

    /**
     * Set to false to show the normal horizontal menu on tablet; default = true.
     */
    public readonly showHamburgerOnTablet = input(true);

    /**
     * Is user logged in?
     * If not set no login will be shown.
     * If false login-button will be shown.
     * If true logged in state will be shown
     */
    public readonly isLoggedIn = input<boolean>();

    /**
     * Profile menu with user data
     */
    public readonly profileMenu = input<ProfileMenu>();

    /**
     * Emits the navigationItem that is clicked.
     */
    public readonly navigationClicked = output<NavigationItem>();

    /**
     * Emits the click event on search.
     */
    public readonly searchClicked = output<void>();

    /**
     * Emits event to login
     */
    public readonly loginLogout = output<boolean>();

    /**
     * Event-emitter that is emitted on click, keyboard enter and keyboard space
     * Emits clicked profile menu item
     */
    public readonly profileItemClicked = output<ProfileMenuItem>();

    protected readonly menuOpen = signal(false);
    protected readonly profileMenuOpen = signal(false);
    protected theme: string;

    constructor() {
        this.contentService.addContentItems(content);
        this.theme = this.themeService.getTheme();
    }

    protected toggleProfileMenu(): void {
        this.menuOpen.set(false);
        this.profileMenuOpen.set(!this.profileMenuOpen());
    }

    protected toggleMenu(): void {
        this.profileMenuOpen.set(false);
        this.menuOpen.set(!this.menuOpen());
    }

    /**
     * Close profile menu and emit clicked item
     */
    protected navigateProfileMenu(item: ProfileMenuItem): void {
        this.profileMenuOpen.set(false);
        this.profileItemClicked.emit(item);
        this.blurFocus();
    }

    protected navigate(navigationItem: NavigationItem) {
        if (navigationItem.type === NavigationType.SEARCH) {
            this.searchClicked.emit(undefined);
            return;
        } else if (navigationItem.link) {
            this.menuOpen.set(false);
            if (navigationItem.type === NavigationType.LINK_ICON) {
                window.location.href = navigationItem.link;
            }
        }
        this.navigationClicked.emit(navigationItem);
    }

    protected hasNavigationItems() {
        return (this.navigationItems() != null && this.navigationItems().length > 0) || this.isLoggedIn() !== null;
    }

    protected getSearchItem() {
        return this.navigationItems().find((d) => d.type === NavigationType.SEARCH) || null;
    }

    protected getCanvasModifier(): string {
        const layout = this.layout();
        return layout === 'default' ? '' : `ap-header__canvas--${layout}`;
    }

    private blurFocus() {
        (this.document.activeElement as HTMLElement)?.blur();
    }
}
